import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGame } from "@/context/GameContext";
import { playSfx } from "@/lib/sounds";
import { 
  ArrowLeft, Volume2, VolumeX, Star, Wallet, Gift, ShoppingBag, 
  CheckCircle2, Heart, Coins, Trophy, Sparkles, RefreshCw, Award
} from 'lucide-react';
import confetti from 'canvas-confetti';

const FOODS = [
  { id: 1, icon: '🍌', type: 'healthy', name: 'Pisang' },
  { id: 2, icon: '🍭', type: 'unhealthy', name: 'Permen' },
  { id: 3, icon: '🥕', type: 'healthy', name: 'Wortel' },
  { id: 4, icon: '🍨', type: 'unhealthy', name: 'Es Krim' },
  { id: 5, icon: '🥛', type: 'healthy', name: 'Susu' },
  { id: 6, icon: '🥤', type: 'unhealthy', name: 'Soda' }
];

const QUIZ_ROUNDS_M2 = [
  { q: "Manis mana yang alami dan sehat?", options: [{ id: 'a', icon: '🍌', type: 'natural', name: 'Pisang' }, { id: 'b', icon: '🍫', type: 'artificial', name: 'Cokelat Olahan' }] },
  { q: "Mana minuman yang lebih alami?", options: [{ id: 'a', icon: '🥤', type: 'artificial', name: 'Soda Manis' }, { id: 'b', icon: '🍊', type: 'natural', name: 'Jus Jeruk Asli' }] },
  { q: "Mana camilan manis teman sehat tubuh?", options: [{ id: 'a', icon: '🌴', type: 'natural', name: 'Kurma' }, { id: 'b', icon: '🍬', type: 'artificial', name: 'Jelly Buatan' }] }
];

const ANIMALS_M4 = [
  { id: 1, icon: '🐰', name: 'Kelinci Ceria', task: 'Lompat 3 kali!', action: 'LOMPAT' },
  { id: 2, icon: '🦅', name: 'Burung Gagah', task: 'Kibaskan tangan!', action: 'KIBAS' },
  { id: 3, icon: '🐸', name: 'Katak Lincah', task: 'Lompat jauh!', action: 'HOP' }
];

const JARS_M5 = [
  { id: 'saving', color: 'bg-blue-500 border-blue-600', icon: <Wallet className="text-white w-6 h-6"/>, title: 'TABUNGAN' },
  { id: 'sharing', color: 'bg-emerald-500 border-emerald-600', icon: <Gift className="text-white w-6 h-6"/>, title: 'BERBAGI' },
  { id: 'spending', color: 'bg-amber-400 border-amber-500', icon: <ShoppingBag className="text-white w-6 h-6"/>, title: 'JAJAN' }
];

const RECAP_MISSIONS = [
  { id: 1, icon: '🔍', title: 'Detektif Rasa', desc: 'Memilih makanan sehat' },
  { id: 2, icon: '🧪', title: 'Rasa Manis', desc: 'Mengenal gula alami' },
  { id: 3, icon: '💧', title: 'Ginjal Bersinar', desc: 'Rutin minum air putih' },
  { id: 4, icon: '🏃‍♂️', title: 'Tubuh Bergerak', desc: 'Olahraga lincah gembira' },
  { id: 5, icon: '👛', title: 'Toples Tiga Warna', desc: 'Menabung & berbagi' },
  { id: 6, icon: '📦', title: 'Kotak Ajaib', desc: 'Belajar bersabar' }
];

export default function HealthAdventure() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCat = searchParams.get('cat') || 'health';

  const { soundOn, toggleSound } = useGame();
  const [category, setCategory] = useState(initialCat); 
  const [mission, setMission] = useState(null); 
  const [page, setPage] = useState(0);
  const [round, setRound] = useState(0);
  const [items, setItems] = useState([]);
  const [feedback, setFeedback] = useState(null);
  
  // Mission 3 state
  const [waterLevel, setWaterLevel] = useState(0);
  
  // Mission 4 state
  const [m4AnimalIdx, setM4AnimalIdx] = useState(0);
  const [m4Count, setM4Count] = useState(0);

  // Mission 5 state
  const [jarCoins, setJarCoins] = useState({ saving: 0, sharing: 0, spending: 0 });
  const [coinsLeft, setCoinsLeft] = useState(3);

  // Mission 6 state
  const [m6Timer, setM6Timer] = useState(null);
  const [m6Countdown, setM6Countdown] = useState(5);
  const [m6Reward, setM6Reward] = useState(null);

  useEffect(() => {
    setCategory(searchParams.get('cat') || 'health');
  }, [searchParams]);

  const triggerVictory = () => {
    playSfx('success', soundOn);
    try {
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
    } catch (e) {}
    setPage(3);
  };

  const startMission = (missionId) => {
    playSfx('pop', soundOn);
    setMission(missionId);
    setPage(1);
    setRound(0);
    setFeedback(null);

    if (missionId === 1) {
      setItems([...FOODS]);
    } else if (missionId === 3) {
      setWaterLevel(0);
    } else if (missionId === 4) {
      setM4AnimalIdx(0);
      setM4Count(0);
    } else if (missionId === 5) {
      setJarCoins({ saving: 0, sharing: 0, spending: 0 });
      setCoinsLeft(3);
    } else if (missionId === 6) {
      setM6Countdown(5);
      setM6Reward(null);
      setM6Timer(null);
    }
  };

  // Mission 1 item sorting
  const handleFoodSelect = (food, targetType) => {
    if (food.type === targetType) {
      playSfx('pop', soundOn);
      const remaining = items.filter(i => i.id !== food.id);
      setItems(remaining);
      setFeedback({ type: 'success', text: 'Pintar! Tepat Sekali! 🎉' });
      if (remaining.length === 0) {
        setTimeout(triggerVictory, 800);
      }
    } else {
      playSfx('error', soundOn);
      setFeedback({ type: 'error', text: 'Ups! Coba lagi ya! 🧐' });
    }
    setTimeout(() => setFeedback(null), 1000);
  };

  // Mission 2 Quiz answer
  const handleQuizAnswer = (option) => {
    if (option.type === 'natural') {
      playSfx('pop', soundOn);
      setFeedback({ type: 'success', text: 'Benar! Manis Alami! 🍎' });
      if (round < QUIZ_ROUNDS_M2.length - 1) {
        setTimeout(() => {
          setRound(r => r + 1);
          setFeedback(null);
        }, 800);
      } else {
        setTimeout(triggerVictory, 800);
      }
    } else {
      playSfx('error', soundOn);
      setFeedback({ type: 'error', text: 'Ini gula olahan buatan, pilih yang alami ya! 🍌' });
      setTimeout(() => setFeedback(null), 1200);
    }
  };

  // Mission 3 Hydration fill
  const fillWater = () => {
    playSfx('pop', soundOn);
    setWaterLevel(prev => {
      const next = prev + 25;
      if (next >= 100) {
        setTimeout(triggerVictory, 600);
        return 100;
      }
      return next;
    });
  };

  // Mission 4 Animal Movement
  const doAnimalAction = () => {
    playSfx('pop', soundOn);
    setM4Count(prev => {
      const next = prev + 1;
      if (next >= 3) {
        if (m4AnimalIdx < ANIMALS_M4.length - 1) {
          setTimeout(() => {
            setM4AnimalIdx(idx => idx + 1);
            setM4Count(0);
          }, 600);
        } else {
          setTimeout(triggerVictory, 600);
        }
      }
      return next;
    });
  };

  // Mission 5 Coin deposit
  const depositCoin = (jarId) => {
    if (coinsLeft <= 0) return;
    playSfx('pop', soundOn);
    setJarCoins(prev => ({ ...prev, [jarId]: prev[jarId] + 1 }));
    setCoinsLeft(prev => {
      const next = prev - 1;
      if (next <= 0) {
        setTimeout(triggerVictory, 800);
      }
      return next;
    });
  };

  // Mission 6 Patience Timer
  const startPatienceTimer = () => {
    playSfx('pop', soundOn);
    setM6Timer(true);
    let count = 5;
    setM6Countdown(5);
    const interval = setInterval(() => {
      count -= 1;
      setM6Countdown(count);
      if (count <= 0) {
        clearInterval(interval);
        setM6Reward('3 Mainan Super Emas 🎁🎁🎁');
        setTimeout(triggerVictory, 1000);
      }
    }, 1000);
  };

  const takeInstantReward = () => {
    playSfx('pop', soundOn);
    setM6Reward('1 Mainan Kecil 🎁');
    setTimeout(triggerVictory, 800);
  };

  return (
    <div className="fixed inset-0 bg-[#F8FAFC] font-sans overflow-hidden flex flex-col items-center justify-center p-2 sm:p-4">
      {/* Top Header Buttons */}
      <div className="absolute top-3 left-3 right-3 flex justify-between z-50">
        <button 
          onClick={() => mission ? (setMission(null), setPage(0), setRound(0)) : navigate('/')} 
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white shadow-lg border border-slate-100 flex items-center justify-center font-bold text-slate-700 hover:bg-slate-50 active:scale-95 transition-all"
        >
          <ArrowLeft size={22}/>
        </button>

        <div className="flex gap-2">
          <button 
            onClick={() => {
              const newCat = category === 'health' ? 'finance' : 'health';
              setCategory(newCat);
              setMission(null);
              setPage(0);
            }} 
            className={`h-10 sm:h-12 px-4 rounded-full shadow-lg font-bold text-xs sm:text-sm flex items-center gap-1.5 transition-all ${
              category === 'health' ? 'bg-blue-600 text-white' : 'bg-rose-500 text-white'
            }`}
          >
            {category === 'health' ? <Coins size={16}/> : <Heart size={16}/>}
            <span>{category === 'health' ? 'Ubah ke Misi Bijak' : 'Ubah ke Misi Sehat'}</span>
          </button>

          <button 
            onClick={toggleSound} 
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white shadow-lg border border-slate-100 flex items-center justify-center text-slate-700 hover:bg-slate-50 active:scale-95 transition-all"
          >
            {soundOn ? <Volume2 size={22} className="text-sky-500"/> : <VolumeX size={22} className="text-slate-400"/>}
          </button>
        </div>
      </div>

      {/* Main Container Card */}
      <div className="w-full max-w-lg bg-white rounded-[2.5rem] sm:rounded-[3rem] border-[6px] sm:border-8 border-[#1E293B] shadow-2xl overflow-y-auto h-[88vh] sm:h-[84vh] flex flex-col items-center p-4 sm:p-8 custom-scrollbar">
        <AnimatePresence mode="wait">
          
          {/* MENU LEVEL SELECTOR */}
          {!mission && (
            <motion.div key="menu" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-center py-4 w-full my-auto">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-100 text-slate-600 font-bold text-xs mb-3">
                {category === 'health' ? <Heart size={14} className="text-rose-500 fill-current"/> : <Coins size={14} className="text-amber-500 fill-current"/>}
                <span>Petualangan Karakter Anak</span>
              </div>

              <h1 className="text-2xl sm:text-3xl font-black text-[#1E293B] mb-6 uppercase tracking-tight italic">
                {category === 'health' ? '🩺 MISI SEHAT' : '💰 MISI BIJAK'}
              </h1>

              <div className="grid grid-cols-1 gap-3 w-full max-w-xs mx-auto pb-6">
                {category === 'health' ? (
                  [
                    { title: 'Detektif Rasa', desc: 'Pilih Makanan Sehat', color: 'bg-emerald-500 shadow-[0_5px_0_#059669]' },
                    { title: 'Rasa Manis', desc: 'Mengenal Gula Alami', color: 'bg-amber-400 shadow-[0_5px_0_#d97706]' },
                    { title: 'Ginjal Bersinar', desc: 'Rutin Minum Air Putih', color: 'bg-sky-500 shadow-[0_5px_0_#0284c7]' },
                    { title: 'Tubuh Bergerak', desc: 'Olahraga & Gerak Lincah', color: 'bg-purple-500 shadow-[0_5px_0_#7e22ce]' },
                  ].map((m, i) => (
                    <button 
                      key={i} 
                      onClick={() => startMission(i + 1)} 
                      className={`p-4 sm:p-5 rounded-3xl text-white font-black text-left flex items-center gap-3 transition-transform active:scale-95 ${m.color}`}
                    >
                      <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center text-lg font-black shrink-0">{i + 1}</div>
                      <div>
                        <div className="text-base sm:text-lg leading-tight">{m.title}</div>
                        <div className="text-[11px] font-bold opacity-80">{m.desc}</div>
                      </div>
                    </button>
                  ))
                ) : (
                  [
                    { title: 'Toples Tiga Warna', desc: 'Tabungan, Berbagi & Jajan', color: 'bg-blue-500 shadow-[0_5px_0_#1d4ed8]' },
                    { title: 'Kotak Ajaib', desc: 'Belajar Sabar & Menunggu', color: 'bg-indigo-500 shadow-[0_5px_0_#4338ca]' },
                    { title: 'Pesta Kemenangan', desc: 'Piala & Rekap Prestasi', color: 'bg-amber-500 shadow-[0_5px_0_#b45309]' },
                  ].map((m, i) => (
                    <button 
                      key={i} 
                      onClick={() => startMission(i + 5)} 
                      className={`p-4 sm:p-5 rounded-3xl text-white font-black text-left flex items-center gap-3 transition-transform active:scale-95 ${m.color}`}
                    >
                      <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center text-lg font-black shrink-0">{i + 5}</div>
                      <div>
                        <div className="text-base sm:text-lg leading-tight">{m.title}</div>
                        <div className="text-[11px] font-bold opacity-80">{m.desc}</div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          )}

          {/* ================================================================= */}
          {/* MISSION 1: DETEKTIF RASA */}
          {/* ================================================================= */}
          {mission === 1 && page === 1 && (
            <motion.div key="m1p1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6 my-auto w-full">
              <div className="text-7xl mb-4 animate-bounce">🍎🥦</div>
              <h1 className="text-2xl font-black text-slate-800 mb-2">Misi 1: Detektif Rasa</h1>
              <p className="font-bold text-slate-500 mb-8 max-w-xs mx-auto text-sm">
                Bantu detektif memisahkan makanan sehat penambah energi dengan jajanan manis berlebihan!
              </p>
              <button 
                onClick={() => setPage(2)} 
                className="w-full py-4 bg-emerald-500 text-white rounded-full text-xl font-black shadow-[0_5px_0_#059669] active:translate-y-1"
              >
                MULAI MISI! 🚀
              </button>
            </motion.div>
          )}

          {mission === 1 && page === 2 && (
            <motion.div key="m1p2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full text-center my-auto">
              <div className="text-xs font-bold text-slate-400 mb-2 uppercase">Pilih Makanan & Ketuk Kategori yang Tepat!</div>
              
              {feedback && (
                <div className={`p-2.5 rounded-2xl mb-4 font-bold text-xs text-white ${feedback.type === 'success' ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                  {feedback.text}
                </div>
              )}

              <div className="bg-slate-50 p-4 rounded-3xl mb-6 min-h-[120px] flex flex-wrap justify-center gap-3 border border-slate-100">
                {items.map((f) => (
                  <div key={f.id} className="bg-white p-3 rounded-2xl shadow-sm border border-slate-200 flex flex-col items-center gap-1.5 w-24">
                    <span className="text-3xl">{f.icon}</span>
                    <span className="text-[11px] font-bold text-slate-700">{f.name}</span>
                    <div className="flex gap-1 w-full mt-1">
                      <button 
                        onClick={() => handleFoodSelect(f, 'healthy')} 
                        className="flex-1 py-1 bg-emerald-100 text-emerald-700 font-bold text-[9px] rounded-lg border border-emerald-300 hover:bg-emerald-200"
                      >
                        SEHAT 🍎
                      </button>
                      <button 
                        onClick={() => handleFoodSelect(f, 'unhealthy')} 
                        className="flex-1 py-1 bg-rose-100 text-rose-700 font-bold text-[9px] rounded-lg border border-rose-300 hover:bg-rose-200"
                      >
                        KURANG 🥤
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* ================================================================= */}
          {/* MISSION 2: RASA MANIS */}
          {/* ================================================================= */}
          {mission === 2 && page === 1 && (
            <motion.div key="m2p1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6 my-auto w-full">
              <div className="text-7xl mb-4 animate-bounce">🍊🍌</div>
              <h1 className="text-2xl font-black text-slate-800 mb-2">Misi 2: Rasa Manis Alami</h1>
              <p className="font-bold text-slate-500 mb-8 max-w-xs mx-auto text-sm">
                Pilihlah sumber rasa manis alami dari buah yang menyehatkan tubuh!
              </p>
              <button 
                onClick={() => setPage(2)} 
                className="w-full py-4 bg-amber-500 text-white rounded-full text-xl font-black shadow-[0_5px_0_#d97706] active:translate-y-1"
              >
                MULAI KUIS! 🌟
              </button>
            </motion.div>
          )}

          {mission === 2 && page === 2 && (
            <motion.div key="m2p2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full text-center my-auto">
              <div className="text-xs font-bold text-amber-600 mb-1 uppercase">Ronde {round + 1} dari {QUIZ_ROUNDS_M2.length}</div>
              <h2 className="text-lg font-black text-slate-800 mb-4">{QUIZ_ROUNDS_M2[round].q}</h2>

              {feedback && (
                <div className={`p-2.5 rounded-2xl mb-4 font-bold text-xs text-white ${feedback.type === 'success' ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                  {feedback.text}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto">
                {QUIZ_ROUNDS_M2[round].options.map((opt) => (
                  <button 
                    key={opt.id} 
                    onClick={() => handleQuizAnswer(opt)}
                    className="bg-amber-50 border-2 border-amber-200 p-4 rounded-3xl flex flex-col items-center gap-2 hover:bg-amber-100 active:scale-95 transition-all shadow-sm"
                  >
                    <span className="text-5xl">{opt.icon}</span>
                    <span className="text-xs font-bold text-slate-800">{opt.name}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* ================================================================= */}
          {/* MISSION 3: GINJAL BERSINAR */}
          {/* ================================================================= */}
          {mission === 3 && page === 1 && (
            <motion.div key="m3p1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6 my-auto w-full">
              <div className="text-7xl mb-4 animate-bounce">💧🥤</div>
              <h1 className="text-2xl font-black text-slate-800 mb-2">Misi 3: Ginjal Bersinar</h1>
              <p className="font-bold text-slate-500 mb-8 max-w-xs mx-auto text-sm">
                Minum air putih secukupnya agar tubuh segar dan ginjal bekerja optimal!
              </p>
              <button 
                onClick={() => setPage(2)} 
                className="w-full py-4 bg-sky-500 text-white rounded-full text-xl font-black shadow-[0_5px_0_#0284c7] active:translate-y-1"
              >
                TETESKAN AIR 💦
              </button>
            </motion.div>
          )}

          {mission === 3 && page === 2 && (
            <motion.div key="m3p2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full text-center my-auto">
              <h2 className="text-lg font-black text-slate-800 mb-2">Isi Gelas Sampai Penuh!</h2>
              <div className="relative w-32 h-44 border-4 border-slate-300 rounded-b-3xl bg-slate-50 mx-auto mb-6 overflow-hidden shadow-inner flex flex-col justify-end">
                <motion.div 
                  animate={{ height: `${waterLevel}%` }} 
                  transition={{ type: "spring", stiffness: 100 }} 
                  className="w-full bg-sky-400 flex items-center justify-center text-white text-xs font-black"
                >
                  {waterLevel}%
                </motion.div>
              </div>

              <button 
                onClick={fillWater}
                className="w-28 h-28 bg-sky-500 text-white rounded-full font-black text-sm shadow-[0_6px_0_#0284c7] active:translate-y-1 uppercase flex flex-col items-center justify-center gap-1 mx-auto"
              >
                <span className="text-2xl">💧</span>
                <span>MINUM!</span>
              </button>
            </motion.div>
          )}

          {/* ================================================================= */}
          {/* MISSION 4: TUBUH BERGERAK */}
          {/* ================================================================= */}
          {mission === 4 && page === 1 && (
            <motion.div key="m4p1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6 my-auto w-full">
              <div className="text-7xl mb-4 animate-bounce">🐰🏃‍♂️</div>
              <h1 className="text-2xl font-black text-slate-800 mb-2">Misi 4: Tubuh Bergerak</h1>
              <p className="font-bold text-slate-500 mb-8 max-w-xs mx-auto text-sm">
                Ikuti gerakan hewan gembira untuk melatih fisik dan ketangkasan anak!
              </p>
              <button 
                onClick={() => setPage(2)} 
                className="w-full py-4 bg-purple-500 text-white rounded-full text-xl font-black shadow-[0_5px_0_#7e22ce] active:translate-y-1"
              >
                MULAI GERAK! 🤸‍♀️
              </button>
            </motion.div>
          )}

          {mission === 4 && page === 2 && (
            <motion.div key="m4p2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full text-center my-auto">
              <div className="text-xs font-bold text-purple-600 mb-1 uppercase">Hewan {m4AnimalIdx + 1} dari {ANIMALS_M4.length}</div>
              <div className="text-6xl mb-2">{ANIMALS_M4[m4AnimalIdx].icon}</div>
              <h2 className="text-xl font-black text-slate-800 mb-1">{ANIMALS_M4[m4AnimalIdx].name}</h2>
              <p className="text-sm font-bold text-purple-600 mb-6">{ANIMALS_M4[m4AnimalIdx].task}</p>

              <button 
                onClick={doAnimalAction}
                className="w-32 h-32 bg-purple-500 text-white rounded-full font-black text-lg shadow-[0_6px_0_#7e22ce] active:translate-y-1 uppercase flex flex-col items-center justify-center gap-1 mx-auto"
              >
                <span>{ANIMALS_M4[m4AnimalIdx].action}!</span>
                <span className="text-xs bg-white/20 px-3 py-0.5 rounded-full">{m4Count} / 3</span>
              </button>
            </motion.div>
          )}

          {/* ================================================================= */}
          {/* MISSION 5: TOPLES TIGA WARNA */}
          {/* ================================================================= */}
          {mission === 5 && page === 1 && (
            <motion.div key="m5p1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6 my-auto w-full">
              <div className="text-7xl mb-4 animate-bounce">👛🎁🛍️</div>
              <h1 className="text-2xl font-black text-slate-800 mb-2">Misi 5: Toples Tiga Warna</h1>
              <p className="font-bold text-slate-500 mb-8 max-w-xs mx-auto text-sm">
                Belajar membagi uang saku ke dalam Toples Tabungan, Berbagi, dan Jajan!
              </p>
              <button 
                onClick={() => setPage(2)} 
                className="w-full py-4 bg-blue-500 text-white rounded-full text-xl font-black shadow-[0_5px_0_#1d4ed8] active:translate-y-1"
              >
                MASUKKAN KOIN 🪙
              </button>
            </motion.div>
          )}

          {mission === 5 && page === 2 && (
            <motion.div key="m5p2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full text-center my-auto">
              <div className="text-xs font-bold text-blue-600 mb-2 uppercase">Koin Tersisa: {coinsLeft} 🪙</div>
              <div className="grid grid-cols-3 gap-2 mb-6">
                {JARS_M5.map((jar) => (
                  <button 
                    key={jar.id}
                    onClick={() => depositCoin(jar.id)}
                    className={`${jar.color} border-b-4 p-3 rounded-2xl text-white flex flex-col items-center gap-1.5 active:scale-95 transition-all shadow-sm`}
                  >
                    {jar.icon}
                    <span className="text-[10px] font-black">{jar.title}</span>
                    <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full font-bold">{jarCoins[jar.id]} Koin</span>
                  </button>
                ))}
              </div>
              <p className="text-xs font-bold text-slate-400 italic">Ketuk salah satu toples di atas untuk memasukkan koin!</p>
            </motion.div>
          )}

          {/* ================================================================= */}
          {/* MISSION 6: KOTAK AJAIB (SABAR) */}
          {/* ================================================================= */}
          {mission === 6 && page === 1 && (
            <motion.div key="m6p1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6 my-auto w-full">
              <div className="text-7xl mb-4 animate-bounce">📦✨</div>
              <h1 className="text-2xl font-black text-slate-800 mb-2">Misi 6: Kotak Ajaib Kesabaran</h1>
              <p className="font-bold text-slate-500 mb-8 max-w-xs mx-auto text-sm">
                Belajar menahan diri! Jika kamu bersabar menunggu 5 detik, hadiahnya makin banyak!
              </p>
              <button 
                onClick={() => setPage(2)} 
                className="w-full py-4 bg-indigo-500 text-white rounded-full text-xl font-black shadow-[0_5px_0_#4338ca] active:translate-y-1"
              >
                UJI KESABARAN! 🎁
              </button>
            </motion.div>
          )}

          {mission === 6 && page === 2 && (
            <motion.div key="m6p2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full text-center my-auto">
              {!m6Timer ? (
                <div>
                  <h2 className="text-lg font-black text-slate-800 mb-6">Pilih Langkahmu!</h2>
                  <div className="flex flex-col gap-3 max-w-xs mx-auto">
                    <button 
                      onClick={takeInstantReward}
                      className="p-4 bg-slate-100 border-2 border-slate-300 text-slate-700 font-bold text-sm rounded-2xl shadow-sm hover:bg-slate-200"
                    >
                      🎁 Buka Sekarang (Dapat 1 Mainan)
                    </button>
                    <button 
                      onClick={startPatienceTimer}
                      className="p-4 bg-indigo-500 text-white font-black text-sm rounded-2xl shadow-[0_4px_0_#4338ca] hover:bg-indigo-600 active:translate-y-0.5"
                    >
                      ⏳ Tunggu 5 Detik (Dapat 3 Mainan Super!)
                    </button>
                  </div>
                </div>
              ) : (
                <div className="py-6">
                  <div className="text-6xl mb-4 animate-pulse">📦✨</div>
                  <h3 className="text-xl font-black text-slate-800 mb-2">Sedang Menunggu Bersabar...</h3>
                  <div className="text-5xl font-black text-indigo-600 my-4">{m6Countdown} Detik</div>
                </div>
              )}
            </motion.div>
          )}

          {/* ================================================================= */}
          {/* MISSION 7: PESTA KEMENANGAN */}
          {/* ================================================================= */}
          {mission === 7 && page === 1 && (
            <motion.div key="m7p1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6 my-auto w-full">
              <div className="text-7xl mb-4 animate-bounce">🏆🎉</div>
              <h1 className="text-2xl font-black text-slate-800 mb-2">Misi 7: Pesta Kemenangan</h1>
              <p className="font-bold text-slate-500 mb-8 max-w-xs mx-auto text-sm">
                Lihat semua pencapaian dan piala kebanggaan anak sehat & bijak!
              </p>
              <button 
                onClick={() => triggerVictory()} 
                className="w-full py-4 bg-amber-500 text-white rounded-full text-xl font-black shadow-[0_5px_0_#b45309] active:translate-y-1"
              >
                LIHAT PIALA! 🥇
              </button>
            </motion.div>
          )}

          {/* ================================================================= */}
          {/* UNIVERSAL MISSION COMPLETED RESULT SCREEN */}
          {/* ================================================================= */}
          {mission > 0 && page === 3 && (
            <motion.div key="res" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6 my-auto w-full">
              <div className="text-7xl mb-4 animate-bounce">🏆✨</div>
              <h2 className="text-2xl font-black mb-2 text-[#1E293B]">Misi Selesai!</h2>
              <p className="text-sm font-bold text-emerald-600 mb-6">Kamu Anak Hebat, Sehat & Bijak! 🎉</p>

              {m6Reward && (
                <div className="bg-amber-50 border-2 border-amber-300 p-3 rounded-2xl mb-6 text-xs font-bold text-amber-800">
                  Hadiah Kesabaran: {m6Reward}
                </div>
              )}

              <button 
                onClick={() => { setMission(null); setPage(0); }} 
                className="w-full py-4 bg-[#1E293B] text-white rounded-full font-black text-lg shadow-[0_5px_0_#0F172A] active:translate-y-1"
              >
                KEMBALI KE MENU 🏠
              </button>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
}
